import { useState } from 'react';
import { Label } from '@verlo/shared/components/ui/label';
import { Copy, Check } from 'lucide-react';
import CodeInput from './CodeInput';

/**
 * Adding an authenticator app (#179): scan the QR code, or type the secret, then enter the
 * first code it shows. Used when one of Verlo's staff signs in without one, and from My settings.
 * `setup` is the API's `{ totp_qr, totp_secret }`.
 */
const TotpSetup = ({ setup, code, onCodeChange, idPrefix = 'totp' }) => {
  const [copied, setCopied] = useState(false);
  // Grouped in fours so it can be read and typed: JBSW Y3DP EHPK 3PXP.
  const grouped = (setup.totp_secret || '').replace(/(.{4})/g, '$1 ').trim();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(setup.totp_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* the text is selectable anyway */
    }
  };

  return (
    <div className="space-y-4">
      <ol className="text-[13px] text-ink-soft space-y-1 list-decimal pl-4">
        <li>Open an authenticator app on your phone, such as Google Authenticator, 1Password or Authy.</li>
        <li>Scan this code, or type the key below.</li>
        <li>Enter the six-digit code the app shows.</li>
      </ol>
      <div className="flex justify-center">
        <img
          src={setup.totp_qr}
          alt="QR code for your authenticator app"
          className="h-44 w-44 rounded border border-hairline bg-white p-2"
          data-testid={`${idPrefix}-qr`}
        />
      </div>
      <div className="flex items-center justify-between gap-2 rounded border border-hairline bg-surface-2 px-3 py-2">
        <code className="font-mono text-[13px] text-ink break-all select-all" data-testid={`${idPrefix}-secret`}>
          {grouped}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 text-ink-faint hover:text-verlo"
          aria-label="Copy the key"
          data-testid={`${idPrefix}-copy`}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-code`} className="text-xs text-ink-soft">Code from the app</Label>
        <CodeInput id={`${idPrefix}-code`} value={code} onChange={onCodeChange} autoFocus={false}
                   data-testid={`${idPrefix}-code`} />
      </div>
    </div>
  );
};

export default TotpSetup;
